/**
 * DraftTicketRenderer — adapter that lets the approval renderer registry
 * dispatch to our bespoke DraftTicketCard.
 *
 * Wraps the card with the `RendererProps` shape (`{ data: unknown }`)
 * and validates the payload before rendering. Falls back to a small
 * inline error if the data isn't shaped as expected.
 */

import type { ReactNode } from 'react'
import { DraftTicketCard } from '../components/DraftTicketCard'
import type { DraftTicketPayload } from '../types'

interface RendererProps {
  data: unknown
}

function isDraftPayload(value: unknown): value is DraftTicketPayload {
  if (!value || typeof value !== 'object') return false
  const obj = value as Record<string, unknown>
  return (
    typeof obj.draft_id === 'string' &&
    typeof obj.title === 'string' &&
    typeof obj.description === 'string' &&
    typeof obj.entity_id === 'string' &&
    typeof obj.type === 'string' &&
    typeof obj.severity === 'string'
  )
}

export function DraftTicketRenderer({ data }: RendererProps): ReactNode {
  if (!isDraftPayload(data)) {
    return (
      <div role="alert" style={errorStyle}>
        Draft ticket data is missing or malformed.
      </div>
    )
  }
  return <DraftTicketCard data={data} variant="compact" />
}

const errorStyle = {
  padding: '10px 12px',
  fontSize: '12px',
  color: 'var(--error)',
  border: '1px solid rgba(239, 68, 68, 0.3)',
  background: 'rgba(239, 68, 68, 0.08)',
  borderRadius: 'var(--radius-sm)',
}

export default DraftTicketRenderer
